import React from 'react';
import {
  FaShieldAlt,
  FaCheckDouble,
  FaCode,
  FaBug,
  FaTachometerAlt,
  FaLock,
  FaUserShield,
  FaChartLine
} from 'react-icons/fa';

const Quality: React.FC = () => {
  const qualityPractices = [
    {
      icon: <FaCheckDouble className="text-3xl" />,
      title: 'Automated Testing',
      description: 'Comprehensive test coverage at every level',
      practices: [
        'Unit tests for all business logic',
        'Integration tests for API endpoints',
        'End-to-end tests for critical user flows',
        'Test-Driven Development (TDD) practices',
        'Minimum 80% code coverage requirement',
        'Automated regression testing'
      ]
    },
    {
      icon: <FaCode className="text-3xl" />,
      title: 'Code Quality',
      description: 'Maintainable, clean code that stands the test of time',
      practices: [
        'Mandatory peer code reviews',
        'Static code analysis and linting',
        'Consistent coding standards and style guides',
        'SOLID principles and design patterns',
        'Regular refactoring and technical debt management',
        'Comprehensive code documentation'
      ]
    },
    {
      icon: <FaShieldAlt className="text-3xl" />,
      title: 'Security First',
      description: 'Security built into every layer of development',
      practices: [
        'OWASP Top 10 vulnerability prevention',
        'Automated security scanning (SAST/DAST)',
        'Dependency vulnerability monitoring',
        'Secure authentication and authorization',
        'Data encryption at rest and in transit',
        'Regular security audits and penetration testing'
      ]
    },
    {
      icon: <FaTachometerAlt className="text-3xl" />,
      title: 'Performance Engineering',
      description: 'Optimized for speed, scalability, and efficiency',
      practices: [
        'Performance testing and benchmarking',
        'Database query optimization',
        'Caching strategies (Redis, CDN)',
        'Load testing for high-traffic scenarios',
        'API response time monitoring',
        'Frontend performance optimization'
      ]
    }
  ];

  const securityFeatures = [
    {
      icon: <FaLock className="text-2xl text-blue-600" />,
      title: 'Data Protection',
      items: [
        'End-to-end encryption',
        'Secure data storage',
        'GDPR & privacy compliance',
        'Regular security patches'
      ]
    },
    {
      icon: <FaUserShield className="text-2xl text-blue-600" />,
      title: 'Access Control',
      items: [
        'Role-based access control (RBAC)',
        'Multi-factor authentication',
        'OAuth2 & SSO integration',
        'Session management'
      ]
    },
    {
      icon: <FaBug className="text-2xl text-blue-600" />,
      title: 'Vulnerability Management',
      items: [
        'Automated dependency scanning',
        'CVE monitoring',
        'Security audit logs',
        'Incident response procedures'
      ]
    },
    {
      icon: <FaChartLine className="text-2xl text-blue-600" />,
      title: 'Monitoring & Alerts',
      items: [
        'Real-time error tracking',
        'Performance monitoring',
        'Security event logging',
        'Proactive alerting'
      ]
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Quality, Security & Testing
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Quality is built into every stage of our development lifecycle through automated testing, peer reviews,
            security-first design, and production monitoring. We don't compromise on reliability.
          </p>
        </div>

        {/* Quality Practices */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {qualityPractices.map((practice, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg p-8 border border-gray-200 hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-start space-x-4 mb-6">
                <div className="bg-blue-100 p-4 rounded-lg text-blue-600 flex-shrink-0">
                  {practice.icon}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {practice.title}
                  </h3>
                  <p className="text-gray-600">
                    {practice.description}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {practice.practices.map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-3 bg-white p-3 rounded-lg">
                    <div className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Security Features Grid */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-12 mb-16">
          <h3 className="text-3xl font-bold text-white mb-8 text-center">
            Enterprise-Grade Security
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {securityFeatures.map((feature, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex flex-col items-center text-center">
                  <div className="bg-white/20 p-4 rounded-lg mb-4">
                    {feature.icon}
                  </div>
                  <h4 className="font-bold text-white mb-4">{feature.title}</h4>
                  <div className="space-y-2 w-full">
                    {feature.items.map((item, idx) => (
                      <div key={idx} className="text-blue-100 text-sm flex items-start space-x-2">
                        <span className="text-white">•</span>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quality Metrics */}
        <div className="bg-gray-50 rounded-2xl p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Our Quality Standards
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-600 mb-2">80%+</div>
              <div className="text-gray-900 font-semibold mb-2">Code Coverage</div>
              <p className="text-gray-600 text-sm">Minimum test coverage for all projects</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-600 mb-2">100%</div>
              <div className="text-gray-900 font-semibold mb-2">Code Review</div>
              <p className="text-gray-600 text-sm">Every line reviewed by senior engineers</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-600 mb-2">Zero</div>
              <div className="text-gray-900 font-semibold mb-2">Known Vulnerabilities</div>
              <p className="text-gray-600 text-sm">At time of deployment</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-600 mb-2">99.9%</div>
              <div className="text-gray-900 font-semibold mb-2">Uptime Target</div>
              <p className="text-gray-600 text-sm">Production system availability</p>
            </div>
          </div>
        </div>

        {/* Development Workflow */}
        <div className="mt-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Quality Gates in Our Workflow
          </h3>
          <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-4">
            <div className="bg-white rounded-lg shadow-md p-6 flex-1 max-w-xs">
              <div className="text-center">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-blue-600 font-bold">1</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Code Development</h4>
                <p className="text-gray-600 text-sm">TDD, linting, local tests</p>
              </div>
            </div>
            <div className="text-gray-400 text-2xl">→</div>
            <div className="bg-white rounded-lg shadow-md p-6 flex-1 max-w-xs">
              <div className="text-center">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-blue-600 font-bold">2</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Peer Review</h4>
                <p className="text-gray-600 text-sm">Code review, feedback</p>
              </div>
            </div>
            <div className="text-gray-400 text-2xl">→</div>
            <div className="bg-white rounded-lg shadow-md p-6 flex-1 max-w-xs">
              <div className="text-center">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-blue-600 font-bold">3</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">CI Pipeline</h4>
                <p className="text-gray-600 text-sm">Automated tests, security scan</p>
              </div>
            </div>
            <div className="text-gray-400 text-2xl">→</div>
            <div className="bg-white rounded-lg shadow-md p-6 flex-1 max-w-xs">
              <div className="text-center">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-blue-600 font-bold">4</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Deployment</h4>
                <p className="text-gray-600 text-sm">Staging, UAT, production</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Quality;
